import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import {ApiContext} from '../../../api/Api';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {NotifierContext} from '../../../utils/Notifier';

const CONFIGURATION_IDS = {
    companyName: 'TECHNOLOGY_IDENTITY_COMPANY_NAME',
    website: 'TECHNOLOGY_IDENTITY_WEBSITE',
    facebook: 'TECHNOLOGY_IDENTITY_FACEBOOK',
    instagram: 'TECHNOLOGY_IDENTITY_INSTAGRAM',
    tiktok: 'TECHNOLOGY_IDENTITY_TIKTOK',
    linkedin: 'TECHNOLOGY_IDENTITY_LINKEDIN',
    youtube: 'TECHNOLOGY_IDENTITY_YOUTUBE'
};

const EMPTY_VALUES = {
    companyName: '',
    website: '',
    facebook: '',
    instagram: '',
    tiktok: '',
    linkedin: '',
    youtube: ''
};

const TechnologyIdentityConfiguration = () => {
    const api = useContext(ApiContext);
    const notifier = useContext(NotifierContext);

    const [values, setValues] = useState(EMPTY_VALUES);
    const [initialValues, setInitialValues] = useState(EMPTY_VALUES);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadConfigurations = useCallback(() => {
        setLoading(true);

        Promise.all(
            Object.entries(CONFIGURATION_IDS).map(
                ([key, id]) =>
                    api.configurations
                        .getSingle({
                            id,
                            loadingId:
                                '@TechnologyIdentityConfiguration.load'
                        })
                        .then(configuration => [
                            key,
                            configuration.value || ''
                        ])
            )
        )
            .then(entries => {
                const loadedValues = Object.fromEntries(entries);

                setValues(loadedValues);
                setInitialValues(loadedValues);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [api]);

    useEffect(() => {
        loadConfigurations();
    }, [loadConfigurations]);

    const hasChanges = useMemo(
        () => JSON.stringify(values) !== JSON.stringify(initialValues),
        [initialValues, values]
    );

    const configuredCount = useMemo(
        () => Object.values(values).filter(
            value => String(value || '').trim()
        ).length,
        [values]
    );

    const updateValue = useCallback(
        (field, value) => {
            setValues(currentValues => ({
                ...currentValues,
                [field]: value
            }));
        },
        []
    );

    const normalizeUrl = value => {
        const cleanValue = String(value || '').trim();

        if (!cleanValue) {
            return '';
        }

        if (
            cleanValue.startsWith('http://')
            || cleanValue.startsWith('https://')
        ) {
            return cleanValue;
        }

        return `https://${cleanValue}`;
    };

    const isValidUrl = value => {
        if (!value) {
            return true;
        }

        try {
            const url = new URL(normalizeUrl(value));

            return ['http:', 'https:'].includes(url.protocol);
        } catch (error) {
            return false;
        }
    };

    const validate = () => {
        if (!values.companyName.trim()) {
            notifier.error(
                'El nombre comercial es obligatorio.'
            );

            return false;
        }

        const urlFields = [
            ['Sitio web', values.website],
            ['Facebook', values.facebook],
            ['Instagram', values.instagram],
            ['TikTok', values.tiktok],
            ['LinkedIn', values.linkedin],
            ['YouTube', values.youtube]
        ];

        const invalidField = urlFields.find(
            ([, value]) => !isValidUrl(value)
        );

        if (invalidField) {
            notifier.error(
                `La dirección de ${invalidField[0]} no es válida.`
            );

            return false;
        }

        return true;
    };

    const save = () => {
        if (!validate()) {
            return;
        }

        const normalizedValues = {
            ...values,
            companyName: values.companyName.trim(),
            website: normalizeUrl(values.website),
            facebook: normalizeUrl(values.facebook),
            instagram: normalizeUrl(values.instagram),
            tiktok: normalizeUrl(values.tiktok),
            linkedin: normalizeUrl(values.linkedin),
            youtube: normalizeUrl(values.youtube)
        };

        setSaving(true);

        api.configurations.batchEdit({
            loadingId:
                '@TechnologyIdentityConfiguration.save',
            configurations: Object.entries(
                CONFIGURATION_IDS
            ).map(([key, id]) => ({
                id,
                value: normalizedValues[key]
            }))
        })
            .then(() => {
                setValues(normalizedValues);
                setInitialValues(normalizedValues);
                setSaving(false);

                notifier.success(
                    'Identidad corporativa guardada correctamente.'
                );
            })
            .catch(() => {
                setSaving(false);
            });
    };

    const reset = () => {
        setValues(initialValues);
    };

    if (loading) {
        return (
            <div className="container-fluid main-container">
                <div
                    style={{
                        padding: '70px 20px',
                        textAlign: 'center'
                    }}
                >
                    <i
                        className="fa fa-spinner fa-spin"
                        style={{
                            fontSize: '34px',
                            color: '#337ab7'
                        }}
                    />

                    <p
                        className="text-muted"
                        style={{marginTop: '16px'}}
                    >
                        Cargando identidad corporativa...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="TechnologyIdentityConfiguration container-fluid main-container"
            style={{paddingBottom: '45px'}}
        >
            <div
                style={{
                    maxWidth: '1160px',
                    margin: '0 auto'
                }}
            >
                <div style={{marginBottom: '25px'}}>
                    <h2 style={{marginBottom: '7px'}}>
                        Identidad Corporativa
                    </h2>

                    <p
                        className="text-muted"
                        style={{
                            margin: 0,
                            fontSize: '15px'
                        }}
                    >
                        Administra la marca, el sitio web y las redes
                        sociales oficiales utilizadas por las tarjetas
                        digitales y firmas institucionales.
                    </p>
                </div>

                <div className="row">
                    <div className="col-md-8">
                        <div
                            style={{
                                background: '#ffffff',
                                border: '1px solid #e5e9ee',
                                borderRadius: '14px',
                                overflow: 'hidden',
                                boxShadow:
                                    '0 5px 20px rgba(32, 45, 64, 0.08)'
                            }}
                        >
                            <div
                                style={{
                                    padding: '21px 24px',
                                    borderBottom: '1px solid #edf0f4',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <div>
                                    <h4
                                        style={{
                                            margin: 0,
                                            fontWeight: 600
                                        }}
                                    >
                                        Configuración general
                                    </h4>

                                    <p
                                        className="text-muted"
                                        style={{
                                            margin: '5px 0 0'
                                        }}
                                    >
                                        Fuente central para toda la identidad
                                        digital de la empresa.
                                    </p>
                                </div>

                                <span
                                    className={
                                        configuredCount === 7
                                            ? 'label label-success'
                                            : 'label label-warning'
                                    }
                                    style={{
                                        padding: '6px 11px',
                                        borderRadius: '12px'
                                    }}
                                >
                                    {configuredCount} de 7 configurados
                                </span>
                            </div>

                            <div style={{padding: '24px'}}>
                                <IdentityField
                                    icon="fa fa-building-o"
                                    label="Nombre comercial"
                                    help={
                                        'Nombre oficial mostrado en tarjetas, vCards y firmas.'
                                    }
                                    value={values.companyName}
                                    required
                                    disabled={saving}
                                    placeholder="The Grand Lounge Elite"
                                    onChange={value =>
                                        updateValue(
                                            'companyName',
                                            value
                                        )
                                    }
                                />

                                <IdentityField
                                    icon="fa fa-globe"
                                    label="Sitio web corporativo"
                                    help={
                                        'Página web oficial de la empresa.'
                                    }
                                    value={values.website}
                                    disabled={saving}
                                    placeholder="https://..."
                                    onChange={value =>
                                        updateValue('website', value)
                                    }
                                />

                                <IdentityField
                                    icon="fa fa-facebook"
                                    label="Facebook corporativo"
                                    help={
                                        'Página oficial de Facebook de la empresa.'
                                    }
                                    value={values.facebook}
                                    disabled={saving}
                                    placeholder="https://facebook.com/..."
                                    onChange={value =>
                                        updateValue('facebook', value)
                                    }
                                />

                                <IdentityField
                                    icon="fa fa-instagram"
                                    label="Instagram corporativo"
                                    help={
                                        'Perfil oficial de Instagram de la empresa.'
                                    }
                                    value={values.instagram}
                                    disabled={saving}
                                    placeholder="https://instagram.com/..."
                                    onChange={value =>
                                        updateValue('instagram', value)
                                    }
                                />

                                <IdentityField
                                    icon="fa fa-music"
                                    label="TikTok corporativo"
                                    help={
                                        'Perfil oficial de TikTok de la empresa.'
                                    }
                                    value={values.tiktok}
                                    disabled={saving}
                                    placeholder="https://tiktok.com/@..."
                                    onChange={value =>
                                        updateValue('tiktok', value)
                                    }
                                />

                                <IdentityField
                                    icon="fa fa-linkedin"
                                    label="LinkedIn corporativo"
                                    help={
                                        'Página institucional de LinkedIn.'
                                    }
                                    value={values.linkedin}
                                    disabled={saving}
                                    placeholder="https://linkedin.com/company/..."
                                    onChange={value =>
                                        updateValue('linkedin', value)
                                    }
                                />

                                <IdentityField
                                    icon="fa fa-youtube-play"
                                    label="YouTube corporativo"
                                    help={
                                        'Canal oficial de contenido institucional.'
                                    }
                                    value={values.youtube}
                                    disabled={saving}
                                    placeholder="https://youtube.com/..."
                                    onChange={value =>
                                        updateValue('youtube', value)
                                    }
                                />
                            </div>

                            <div
                                style={{
                                    padding: '17px 24px',
                                    borderTop: '1px solid #edf0f4',
                                    background: '#fafbfc',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '10px'
                                }}
                            >
                                <CustomButton
                                    bsStyle="default"
                                    disabled={
                                        saving || !hasChanges
                                    }
                                    onClick={reset}
                                >
                                    Descartar cambios
                                </CustomButton>

                                <CustomButton
                                    bsStyle="primary"
                                    disabled={
                                        saving || !hasChanges
                                    }
                                    onClick={save}
                                >
                                    <i
                                        className={
                                            saving
                                                ? 'fa fa-spinner fa-spin'
                                                : 'fa fa-save'
                                        }
                                        style={{marginRight: '7px'}}
                                    />

                                    {saving
                                        ? 'Guardando...'
                                        : 'Guardar configuración'}
                                </CustomButton>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <CorporateIdentityPreview
                            values={values}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const IdentityField = ({
    icon,
    label,
    help,
    value,
    placeholder,
    required,
    disabled,
    onChange
}) => {
    const configured = !!String(value || '').trim();

    return (
        <div
            style={{
                padding: '16px 0',
                borderBottom: '1px solid #edf0f4'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start'
                }}
            >
                <div
                    style={{
                        width: '38px',
                        height: '38px',
                        minWidth: '38px',
                        marginRight: '14px',
                        borderRadius: '10px',
                        background: configured
                            ? '#eaf5fb'
                            : '#f0f2f5',
                        color: configured
                            ? '#337ab7'
                            : '#8d98a5',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '17px'
                    }}
                >
                    <i className={icon} />
                </div>

                <div style={{flex: 1}}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '7px'
                        }}
                    >
                        <label style={{margin: 0}}>
                            {label}
                            {required ? ' *' : ''}
                        </label>

                        <span
                            className={
                                configured
                                    ? 'label label-success'
                                    : 'label label-default'
                            }
                            style={{borderRadius: '10px'}}
                        >
                            {configured
                                ? 'Configurado'
                                : 'Pendiente'}
                        </span>
                    </div>

                    <input
                        type="text"
                        className="form-control"
                        value={value}
                        disabled={disabled}
                        maxLength={1000}
                        placeholder={placeholder}
                        onChange={event =>
                            onChange(event.target.value)
                        }
                    />

                    <p
                        className="help-block"
                        style={{marginBottom: 0}}
                    >
                        {help}
                    </p>
                </div>
            </div>
        </div>
    );
};

const CorporateIdentityPreview = ({values}) => {
    const links = [
        {
            key: 'website',
            icon: 'fa fa-globe',
            label: 'Sitio web'
        },
        {
            key: 'facebook',
            icon: 'fa fa-facebook',
            label: 'Facebook'
        },
        {
            key: 'instagram',
            icon: 'fa fa-instagram',
            label: 'Instagram'
        },
        {
            key: 'tiktok',
            icon: 'fa fa-music',
            label: 'TikTok'
        },
        {
            key: 'linkedin',
            icon: 'fa fa-linkedin',
            label: 'LinkedIn'
        },
        {
            key: 'youtube',
            icon: 'fa fa-youtube-play',
            label: 'YouTube'
        }
    ].filter(item => values[item.key]);

    return (
        <div
            style={{
                position: 'sticky',
                top: '20px',
                borderRadius: '14px',
                overflow: 'hidden',
                background: '#ffffff',
                border: '1px solid #e5e9ee',
                boxShadow:
                    '0 5px 20px rgba(32, 45, 64, 0.08)'
            }}
        >
            <div
                style={{
                    padding: '18px 20px',
                    borderBottom: '1px solid #edf0f4'
                }}
            >
                <h4 style={{margin: 0}}>
                    Vista previa
                </h4>

                <p
                    className="text-muted"
                    style={{margin: '5px 0 0'}}
                >
                    Enlaces corporativos oficiales
                </p>
            </div>

            <div
                style={{
                    padding: '26px 22px',
                    minHeight: '390px',
                    textAlign: 'center',
                    color: '#ffffff',
                    background:
                        'linear-gradient(145deg, #172d50 0%, #28527e 100%)'
                }}
            >
                <i
                    className="fa fa-building-o"
                    style={{
                        fontSize: '44px',
                        color: '#9fe0f7',
                        marginBottom: '18px'
                    }}
                />

                <h3
                    style={{
                        margin: '0 0 9px',
                        fontWeight: 600
                    }}
                >
                    {values.companyName || 'Nombre corporativo'}
                </h3>

                <p
                    style={{
                        opacity: 0.68,
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.9px'
                    }}
                >
                    Identidad Digital Corporativa
                </p>

                <div
                    style={{
                        width: '50px',
                        height: '3px',
                        margin: '22px auto',
                        background: '#77d2f2',
                        borderRadius: '3px'
                    }}
                />

                {!links.length ? (
                    <p style={{opacity: 0.65}}>
                        Agrega los enlaces oficiales para visualizar
                        la identidad corporativa.
                    </p>
                ) : (
                    links.map(link => (
                        <div
                            key={link.key}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '11px',
                                padding: '10px 12px',
                                borderRadius: '10px',
                                background:
                                    'rgba(255, 255, 255, 0.10)',
                                textAlign: 'left'
                            }}
                        >
                            <i
                                className={link.icon}
                                style={{
                                    width: '25px',
                                    marginRight: '9px',
                                    color: '#9fe0f7',
                                    textAlign: 'center'
                                }}
                            />

                            <span
                                style={{
                                    overflow: 'hidden',
                                    fontSize: '12px',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {link.label}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default TechnologyIdentityConfiguration;
